/**
 * Scorecard Spider Charts for Factor Scores.
 *
 * Renders Highcharts polar/spider charts showing Team Average and Analyst
 * factor scores for each scorecard factor type (Quality, Growth, etc.).
 */

import type Highcharts from "highcharts";
import { isDarkMode, setupThemeObserver } from "../../common/chartTheme";
import { createChartLifecycle } from "./chartLifecycle";

/** A factor label entry from the scorecard data. */
interface FactorLabel {
  id: number;
  type: string;
  type_slug?: string;
  label: string;
}

/** Scorecard chart data as embedded by the Django template via json_script. */
export interface ScorecardChartData {
  labels: FactorLabel[];
  analyst_overall_score: number | null;
  analyst_factor_scores: Record<number, number> | null;
  overall_score: number;
  factor_scores: Record<number, number>;
}

/** Theme colors for the spider chart. */
interface SpiderThemeColors {
  textColor: string;
  gridLineColor: string;
  teamAvgColor: string;
  analystColor: string;
}

let themeObserver: MutationObserver | null = null;

function getThemeColors(): SpiderThemeColors {
  const dark = isDarkMode();
  return {
    textColor: dark ? "#E0E0E3" : "#333333",
    gridLineColor: dark ? "#374151" : "#e5e7eb",
    teamAvgColor: dark ? "#60a5fa" : "#3b82f6",
    analystColor: dark ? "#4ade80" : "#22c55e",
  };
}

function normalizeFactorTypeIdentifier(value: string): string {
  return value.toLowerCase().replace(/[^0-9a-zA-Z]/g, "_");
}

function factorLabelMatchesType(factor: FactorLabel, factorType: string): boolean {
  if (factor.type_slug?.toLowerCase() === factorType.toLowerCase()) {
    return true;
  }

  return normalizeFactorTypeIdentifier(factor.type) === normalizeFactorTypeIdentifier(factorType);
}

function renderChartForContainer(container: HTMLElement): void {
  const configAttr = container.dataset.chartConfig;
  const factorType = container.dataset.factorType;
  if (!configAttr || !factorType) {
    lifecycle.destroyChart(container.id);
    return;
  }

  try {
    const scriptEl = document.getElementById(configAttr);
    if (!scriptEl) {
      lifecycle.destroyChart(container.id);
      return;
    }

    const data = JSON.parse(scriptEl.textContent || "{}") as ScorecardChartData;
    if (!data?.labels || data.labels.length === 0) {
      lifecycle.destroyChart(container.id);
      return;
    }

    if (!lifecycle.isContainerVisible(container)) {
      lifecycle.destroyChart(container.id);
      return;
    }

    lifecycle.destroyChart(container.id);
    const chart = renderScorecardSpiderChart(container, data, factorType);
    if (chart) {
      lifecycle.instances.set(container.id, chart);
    }
  } catch (e) {
    console.warn(`Failed to parse scorecard chart data for ${container.id}:`, e);
  }
}

const lifecycle = createChartLifecycle(".scorecard-spider-chart", renderChartForContainer);

/**
 * Renders a scorecard spider chart.
 *
 * @param container - The container HTML element.
 * @param data - The scorecard chart data from the Django template.
 * @param factorType - The factor type slug (e.g. "quality").
 * @returns The Highcharts chart instance, or null if rendering failed.
 */
export function renderScorecardSpiderChart(
  container: HTMLElement,
  data: ScorecardChartData,
  factorType: string,
): Highcharts.Chart | null {
  if (!container || !window.Highcharts) return null;

  const colors = getThemeColors();

  // Filter labels for this factor type
  const labels: string[] = [];
  const labelIds: number[] = [];

  for (const factor of data.labels) {
    if (factorLabelMatchesType(factor, factorType)) {
      labelIds.push(factor.id);
      labels.push(factor.label);
    }
  }

  if (labels.length === 0) return null;

  // Build series data
  const teamAvgData: Array<number | null> = [];
  const analystData: Array<number | null> = [];

  for (const labelId of labelIds) {
    teamAvgData.push(data.factor_scores?.[labelId] ?? null);
    analystData.push(data.analyst_factor_scores?.[labelId] ?? null);
  }

  // Pad to minimum 5 entries for a proper polygon shape
  while (labels.length < 5) {
    labels.push("");
    teamAvgData.push(null);
    analystData.push(null);
  }

  const hasTeamData = teamAvgData.some((v) => v !== null);
  const hasAnalystData = analystData.some((v) => v !== null);
  if (!hasTeamData && !hasAnalystData) return null;

  const series: Highcharts.SeriesOptionsType[] = [];

  if (hasTeamData) {
    series.push({
      type: "line",
      name: "Team Avg",
      data: teamAvgData,
      pointPlacement: "on",
      color: colors.teamAvgColor,
      lineWidth: 2,
      marker: { enabled: true, radius: 3, symbol: "circle" },
    });
  }

  if (hasAnalystData) {
    series.push({
      type: "line",
      name: "Analyst",
      data: analystData,
      pointPlacement: "on",
      color: colors.analystColor,
      lineWidth: 2,
      dashStyle: "ShortDash" as Highcharts.DashStyleValue,
      marker: { enabled: true, radius: 3, symbol: "diamond" },
    });
  }

  const chart = window.Highcharts.chart(container, {
    chart: {
      polar: true,
      type: "line",
      backgroundColor: "transparent",
      height: 280,
      spacing: [10, 10, 10, 10],
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
    pane: { size: "80%" },
    xAxis: {
      categories: labels,
      tickmarkPlacement: "on",
      lineWidth: 0,
      labels: {
        style: {
          color: colors.textColor,
          fontSize: "11px",
        },
      },
    },
    yAxis: {
      gridLineInterpolation: "polygon",
      gridLineColor: colors.gridLineColor,
      tickInterval: 1,
      min: 0,
      max: 10,
      labels: {
        style: {
          color: colors.textColor,
          fontSize: "9px",
        },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      pointFormat:
        '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.1f}</b><br/>',
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: colors.textColor,
        fontSize: "11px",
        fontWeight: "normal",
      },
    },
    plotOptions: {
      line: {
        animation: { duration: 400 },
      },
    },
    series,
    responsive: {
      rules: [
        {
          condition: { maxWidth: 400 },
          chartOptions: {
            pane: { size: "65%" },
            xAxis: {
              labels: { style: { fontSize: "9px" } },
            },
          },
        },
      ],
    },
  } as Highcharts.Options) as Highcharts.Chart;

  return chart;
}

/**
 * Initializes all scorecard spider charts on the page.
 *
 * Finds all elements with the .scorecard-spider-chart class and renders
 * polar charts based on their data-chart-config and data-factor-type attributes.
 */
export function initializeScorecardSpiderCharts(): void {
  const chartContainers = document.querySelectorAll<HTMLElement>(".scorecard-spider-chart");

  const activeIds = new Set<string>();

  chartContainers.forEach((container) => {
    if (!container.id) return;
    activeIds.add(container.id);
    renderChartForContainer(container);
  });

  // Clean up stale chart instances
  for (const containerId of lifecycle.instances.keys()) {
    if (!activeIds.has(containerId) || !document.getElementById(containerId)) {
      lifecycle.destroyChart(containerId);
    }
  }

  // Always keep the theme observer alive — charts may be recreated later
  // via tab reflow, and they need theme switching to work.
  if (!themeObserver) {
    themeObserver = setupThemeObserver(() => {
      initializeScorecardSpiderCharts();
    });
  }
}

/**
 * Registers DOM listeners to auto-initialize scorecard spider charts.
 */
export function setupScorecardSpiderChartAutoInit(): void {
  lifecycle.setupAutoInit(initializeScorecardSpiderCharts);
}
